import * as React from 'react';
import {
  Box,
  Button,
  Spinner,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { Link } from '@leagueplatform/routing';
import { useLocation, useParams } from 'react-router-dom';
import type { BulkActionControllerStatus } from '@web-config-app/core-react';
import { type BulkActionOperationType } from '@web-config-app/core';

const renderStrong = (chunks: React.ReactNode) => <strong>{chunks}</strong>;

const ENVIRONMENTS_PATH_SEGMENT = 'environments';

type FooterPolicyState =
  | 'running'
  | 'completedSuccess'
  | 'completedWithFailures'
  | 'failed';

interface DoneActionContext {
  pathname: string;
  search: string;
  environmentKey?: string;
  targetEnvironment?: string;
}

type DoneActionConfig = {
  type: 'button' | 'link';
  priority: 'primary' | 'secondary';
  messageId: string;
  resolveHref?: (ctx: DoneActionContext) => string | undefined;
};

type ResolvedDoneAction = {
  type: 'button' | 'link';
  priority: 'primary' | 'secondary';
  messageId: string;
  href?: string;
  onClick?: () => void;
};

type FooterPolicy = {
  showCancel: boolean;
  showLoadingMessage: boolean;
  showRetry: boolean;
  doneActions: DoneActionConfig[];
};

type ResolvedFooterPolicy = Omit<FooterPolicy, 'doneActions'> & {
  doneActions: ResolvedDoneAction[];
};

/**
 * Builds a new pathname by replacing the current environment segment with the
 * target environment. Uses segment-based replacement rather than string replace
 * to avoid partial substring matches (e.g. an environment key that appears
 * elsewhere in the path).
 */
const swapEnvironmentInPath = (ctx: DoneActionContext): string | undefined => {
  if (!ctx.environmentKey || !ctx.targetEnvironment) return undefined;
  const segments = ctx.pathname.split('/');
  const environmentsIndex = segments.indexOf(ENVIRONMENTS_PATH_SEGMENT);
  if (
    environmentsIndex === -1 ||
    segments[environmentsIndex + 1] !== ctx.environmentKey
  ) {
    return undefined;
  }
  segments[environmentsIndex + 1] = ctx.targetEnvironment;
  const newPathname = segments.join('/');
  return ctx.search ? `${newPathname}${ctx.search}` : newPathname;
};

const running: FooterPolicy = {
  showCancel: true,
  showLoadingMessage: true,
  showRetry: false,
  doneActions: [],
};

const completedSuccess: FooterPolicy = {
  showCancel: false,
  showLoadingMessage: false,
  showRetry: false,
  doneActions: [{ type: 'button', priority: 'primary', messageId: 'DONE' }],
};

const failed: FooterPolicy = {
  showCancel: false,
  showLoadingMessage: false,
  showRetry: true,
  doneActions: [{ type: 'button', priority: 'primary', messageId: 'DONE' }],
};

const defaults: Record<FooterPolicyState, FooterPolicy> = {
  running,
  completedSuccess,
  completedWithFailures: failed,
  failed,
};

const copyToEnvDoneActions: DoneActionConfig[] = [
  { type: 'button', priority: 'secondary', messageId: 'DONE' },
  {
    type: 'link',
    priority: 'primary',
    messageId: 'BULK_ACTION_REPORT_SWITCH_ENVIRONMENT',
    resolveHref: swapEnvironmentInPath,
  },
];

const policies: Record<
  BulkActionOperationType,
  Record<FooterPolicyState, FooterPolicy>
> = {
  import: defaults,
  export: defaults,
  copyToEnvironment: {
    ...defaults,
    completedSuccess: {
      ...completedSuccess,
      doneActions: copyToEnvDoneActions,
    },
    completedWithFailures: { ...failed, doneActions: copyToEnvDoneActions },
    failed: { ...failed, doneActions: copyToEnvDoneActions },
  },
  clone: defaults,
  publish: defaults,
  archive: defaults,
  delete: defaults,
};

const getFooterPolicy = ({
  operationType,
  controllerStatus,
  hasFailures,
  onDone,
  ctx,
}: {
  operationType: BulkActionOperationType;
  controllerStatus: BulkActionControllerStatus;
  hasFailures: boolean;
  onDone?: () => void;
  ctx: DoneActionContext;
}): ResolvedFooterPolicy => {
  let state: FooterPolicyState = 'running';
  if (controllerStatus === 'completed') {
    state = hasFailures ? 'completedWithFailures' : 'completedSuccess';
  } else if (controllerStatus === 'failed') {
    state = 'failed';
  }

  const policy = policies[operationType][state];

  const doneActions = policy.doneActions.reduce<ResolvedDoneAction[]>(
    (result, action) => {
      const { type, priority, messageId } = action;

      if (type === 'link') {
        const href = action.resolveHref?.(ctx);
        if (href) {
          return [...result, { type, priority, messageId, href }];
        }
        return result;
      }

      if (onDone) {
        return [...result, { type, priority, messageId, onClick: onDone }];
      }

      return result;
    },
    [],
  );

  // When the primary action was filtered out (e.g. link couldn't resolve),
  // promote the first remaining action to primary so the footer always has
  // a clear primary CTA.
  const hasPrimary = doneActions.some(
    (action) => action.priority === 'primary',
  );
  if (!hasPrimary && doneActions.length > 0) {
    doneActions[0] = { ...doneActions[0], priority: 'primary' };
  }

  return { ...policy, doneActions };
};

const operationTypeMessageMap: Record<BulkActionOperationType, { id: string }> =
  {
    import: { id: 'BULK_ACTION_REPORT_OPERATION_TYPE_IMPORT' },
    export: { id: 'BULK_ACTION_REPORT_OPERATION_TYPE_EXPORT' },
    copyToEnvironment: { id: 'BULK_ACTION_REPORT_OPERATION_TYPE_COPY' },
    clone: { id: 'BULK_ACTION_REPORT_OPERATION_TYPE_DUPLICATE' },
    publish: { id: 'BULK_ACTION_REPORT_OPERATION_TYPE_PUBLISH' },
    archive: { id: 'BULK_ACTION_REPORT_OPERATION_TYPE_ARCHIVE' },
    delete: { id: 'BULK_ACTION_REPORT_OPERATION_TYPE_DELETE' },
  };

export interface BulkActionReportingModalFooterProps {
  operationType: BulkActionOperationType;
  controllerStatus: BulkActionControllerStatus;
  hasFailures: boolean;
  targetEnvironment?: string;
  onCancel?: () => void;
  onRetry?: () => void;
  onDone?: () => void;
}

export const BulkActionReportingModalFooter = ({
  operationType,
  controllerStatus,
  hasFailures,
  targetEnvironment,
  onCancel,
  onRetry,
  onDone,
}: BulkActionReportingModalFooterProps) => {
  const { formatMessage } = useIntl();
  const { pathname, search } = useLocation();
  const { environmentKey } = useParams<{ environmentKey: string }>();

  const policy = getFooterPolicy({
    operationType,
    controllerStatus,
    hasFailures,
    onDone,
    ctx: { pathname, search, environmentKey, targetEnvironment },
  });

  const cancelCTA = policy.showCancel ? onCancel : undefined;
  const retryCTA = policy.showRetry ? onRetry : undefined;
  const operationLabel = formatMessage({
    id: operationTypeMessageMap[operationType].id,
  });

  const hasStart = !!cancelCTA;
  const hasEnd =
    policy.showLoadingMessage || policy.doneActions.length > 0 || !!retryCTA;

  if (!hasStart && !hasEnd) {
    return null;
  }

  return (
    <StackLayout
      orientation="horizontal"
      css={{
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Box>
        <StackLayout
          orientation="horizontal"
          spacing="$oneAndQuarter"
          css={{ alignItems: 'center' }}
        >
          {cancelCTA && (
            <Button
              priority="secondary"
              size="medium"
              quiet
              onClick={cancelCTA}
            >
              {formatMessage({ id: 'CANCEL' })}
            </Button>
          )}
        </StackLayout>
      </Box>
      <Box>
        <StackLayout
          orientation="horizontal"
          spacing="$one"
          css={{ alignItems: 'center' }}
        >
          {policy.showLoadingMessage && (
            <StackLayout
              orientation="horizontal"
              spacing="$half"
              verticalAlignment="center"
              aria-live="polite"
            >
              <Spinner loading size="$one" />
              <UtilityText emphasis="subtle">
                {formatMessage(
                  { id: 'BULK_ACTION_REPORTING_PROGRESS_LOADING_MESSAGE' },
                  {
                    operation: operationLabel,
                    strong: renderStrong,
                  },
                )}
              </UtilityText>
            </StackLayout>
          )}
          {retryCTA && (
            <Button priority="secondary" onClick={retryCTA}>
              {formatMessage({ id: 'RETRY' })}
            </Button>
          )}
          {policy.doneActions.map((action) => {
            if (action.href) {
              return (
                <Button
                  key={`${action.type}-${action.messageId}`}
                  as={Link}
                  priority={action.priority}
                  to={action.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {formatMessage({ id: action.messageId })}
                </Button>
              );
            }

            return (
              <Button
                key={`${action.type}-${action.messageId}`}
                priority={action.priority}
                onClick={action.onClick}
              >
                {formatMessage({ id: action.messageId })}
              </Button>
            );
          })}
        </StackLayout>
      </Box>
    </StackLayout>
  );
};
